'use client';

import { MemberStatus } from '@prisma/client';
import { Button, Tooltip, TooltipContent, TooltipTrigger, useSidebar } from '@zond/ui';
import { Clock, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMembers } from '../../../modules/members/members.queries';
import { InviteMembersDialog } from '../../../modules/settings/members/invite-members-dialog';

export const InviteUsersPrompt: React.FC = () => {
  const { t } = useTranslation('navigation');
  const { data: members, refetch } = useGetMembers();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { state } = useSidebar();

  const handleSuccess = () => {
    refetch();
  };

  const getActiveMembers = () => {
    return members?.filter((member) => member.status === MemberStatus.Active) || [];
  };

  const getPendingMembers = () => {
    return members?.filter((member) => member.status === MemberStatus.Invited) || [];
  };

  const formatMemberCount = (count: number) => {
    if (count > 999) return '999+';
    return count.toString();
  };

  const getTeamMessage = (activeCount: number) => {
    if (activeCount === 0) {
      return t('membersWidget.messages.zero');
    }
    if (activeCount === 1) {
      return t('membersWidget.messages.one');
    }
    if (activeCount >= 2 && activeCount <= 4) {
      return t('membersWidget.messages.few');
    }
    return t('membersWidget.messages.many');
  };

  const activeMembers = getActiveMembers();
  const pendingMembers = getPendingMembers();
  const isCollapsed = state === 'collapsed';

  const collapsedContent = (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center justify-center p-2">
          <Button variant="ghost" size="icon" className="relative" onClick={() => setIsDialogOpen(true)}>
            <Users className="h-4 w-4" />
            <div className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/10 px-1 text-[10px] font-medium text-primary">
              {formatMemberCount(activeMembers.length)}
            </div>
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" align="center">
        <div className="space-y-1">
          <div className="font-medium">{t('membersWidget.activeCount', { count: activeMembers.length })}</div>
          {pendingMembers.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {t('membersWidget.pendingCount', { count: pendingMembers.length })}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );

  const expandedContent = (
    <div className="p-3">
      <div className="rounded-xl border bg-card text-card-foreground shadow-none p-4">
        <div className="space-y-3">
          {/* Header */}
          <div>
            <h3 className="text-sm font-semibold">{t('membersWidget.title')}</h3>
          </div>

          {/* Active Members Row */}
          <div className="flex items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Users className="h-4 w-4 text-primary shrink-0" />
              <h4 className="text-sm font-medium truncate">{t('membersWidget.active')}</h4>
            </div>
            <div className="flex items-center justify-center h-4 min-w-[1rem] rounded-full bg-primary/10 text-primary font-medium ml-3 px-1.5 shrink-0 text-[10px] leading-none">
              {formatMemberCount(activeMembers.length)}
            </div>
          </div>

          {/* Pending Members Row */}
          {pendingMembers.length > 0 && (
            <div className="flex items-center">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <h4 className="text-sm font-medium truncate text-muted-foreground">
                  {t('membersWidget.pending')}
                </h4>
              </div>
              <div className="flex items-center justify-center h-4 min-w-[1rem] rounded-full bg-primary/10 text-primary font-medium ml-3 px-1.5 shrink-0 text-[10px] leading-none">
                {formatMemberCount(pendingMembers.length)}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">{getTeamMessage(activeMembers.length)}</p>
          </div>
          <InviteMembersDialog
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onSuccess={handleSuccess}
            triggerButtonProps={{
              variant: 'default',
              className: 'w-full',
              children: (
                <div className="flex items-center justify-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>{t('membersWidget.invite')}</span>
                </div>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isCollapsed ? collapsedContent : expandedContent}
      {isCollapsed && (
        <InviteMembersDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSuccess={handleSuccess}
          triggerButtonProps={{
            variant: 'default',
            className: 'hidden',
          }}
        />
      )}
    </>
  );
};
